import { useMemo } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { groupBy, path, pipe, prop, sortBy, toPairs } from 'ramda';
import { Accordion, AText, Box, ICONS_LOCATIONS } from '@alf/uikit';
import { LINE_VIEW } from '../../constants';
import { useSportsbookStore } from '../../store';
import { MarketsTypeSection } from './MarketsTypeSection';
import { useMarketDescription } from './useMarketDescription';

import styles from './index.module.css';

export const MarketsGroup = ({
  title: titleText,
  listOfMarkets,
  lineView,
  eventId = ''
}) => {
  const outcomes = useSportsbookStore('outcomes');

  const marketsByResultType = useMemo(
    () =>
      pipe(
        groupBy(prop('resultTypeName')),
        toPairs,
        sortBy(path([1, 0, 'resultTypeWeigh']))
      )(listOfMarkets),
    [listOfMarkets]
  );

  const marketsOutcomes = useMemo(
    () =>
      listOfMarkets.reduce(
        (acc, { marketId, outcomeIds, resultTypeId }) => ({
          ...acc,
          ...Object.values(outcomes).reduce(
            (outcomeAcc, outcome) =>
              outcomeIds.includes(outcome.outcomeId)
                ? {
                    ...outcomeAcc,
                    [outcome.outcomeId]: { marketId, resultTypeId, ...outcome }
                  }
                : outcomeAcc,
            {}
          )
        }),
        {}
      ),
    [listOfMarkets, outcomes]
  );

  const { descrTrigger, descrTooltip } = useMarketDescription(
    listOfMarkets?.[0]?.marketTemplateId,
    listOfMarkets?.[0]?.serviceId
  );

  const titleTextNode = (
    <AText
      breed={lineView === LINE_VIEW.TABLE ? 'R' : 'B'}
      size="l"
      color="line_header_txt"
    >
      {titleText}
    </AText>
  );

  const title = descrTrigger ? (
    <Box align="center" gap="s">
      {titleTextNode}
      {descrTrigger}
    </Box>
  ) : (
    titleTextNode
  );

  return (
    listOfMarkets.length > 0 && (
      <Accordion
        dataRole="markets-group"
        arrowsLocation={ICONS_LOCATIONS.END}
        arrowsColor="line_header_txt"
        title={title}
        withArrows={true}
        isOpenDefault={true}
        classNames={{
          headerClassName: clsx(styles.accordion, lineView && styles[lineView]),
          containerClassName: clsx(
            styles.accordionContainer,
            lineView && styles[lineView]
          ),
          bodyClassName: styles.accordionBodyClassName
        }}
        middleContent={descrTooltip}
      >
        <Box
          direction="column"
          className={clsx(styles.container, lineView && styles[lineView])}
        >
          {marketsByResultType.map(([marketTitle, list]) => (
            <MarketsTypeSection
              key={marketTitle}
              title={marketTitle}
              listOfMarkets={list}
              marketsOutcomes={marketsOutcomes}
              lineView={lineView}
              eventId={eventId}
            />
          ))}
        </Box>
      </Accordion>
    )
  );
};

MarketsGroup.propTypes = {
  title: PropTypes.string,
  listOfMarkets: PropTypes.array.isRequired
};
